import { gql } from '@apollo/client';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import LogoHorizontalIcon from '@moda/icons/logo-horizontal-189-x-12';
import { Constrain } from '@moda/om';
import { useLocation } from 'react-router-dom';
import { DesktopSiteNavFragment } from '../../generated/types/DesktopSiteNavFragment';
import { Link } from '../Link';
import { DesktopSiteNavActions } from './DesktopSiteNavActions';
import { DesktopSiteNavItem, DESKTOP_NAV_ITEM_FRAGMENT } from './DesktopSiteNavItem';
import { DesktopSiteNavItemLoading } from './DesktopSiteNavItemLoading';

import './DesktopSiteNav.scss';

export const DESKTOP_SITE_NAV_FRAGMENT = gql`
  fragment DesktopSiteNavFragment on DisplayPage {
    navItems {
      ...DesktopSiteNavItemFragment
    }
  }
  ${DESKTOP_NAV_ITEM_FRAGMENT}
`;

export interface Props {
  displayPage?: DesktopSiteNavFragment | null;
}

const ACTIVATE_DELAY_MS = 250;

export const DesktopSiteNav: React.FC<Props> = ({ displayPage, ...rest }) => {
  const location = useLocation();

  const navItems = displayPage?.navItems;

  const [active, setActive] = useState(false);

  const interval = useRef<NodeJS.Timeout>();

  const handleMouseEnter = useCallback(
    () => (interval.current = global.setTimeout(() => setActive(true), ACTIVATE_DELAY_MS)),
    []
  );

  const handleMouseLeave = useCallback(() => {
    interval.current && clearInterval(interval.current);
    setActive(false);
  }, []);

  useEffect(() => {
    return () => interval.current && clearInterval(interval.current);
  }, []);

  // When the route changes, always close the nav
  useEffect(() => setActive(false), [location]);

  return (
    <nav className="DesktopSiteNav" {...rest}>
      <Constrain className="DesktopSiteNav__primary">
        {/* temporary empty div until new design for nav */}
        <div />
        <div className="DesktopSiteNav__logo" data-testid="logo">
          <Link aria-label="Moda Operandi Home Page" to="/">
            <LogoHorizontalIcon width="100%" height="100%" />
          </Link>
        </div>

        <DesktopSiteNavActions />
      </Constrain>

      <div
        className="DesktopSiteNav__nav-items"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid="desktopNavItems"
      >
        {navItems ? (
          navItems.map((navItem, index) => (
            <DesktopSiteNavItem
              className="DesktopSiteNav__nav-item"
              key={navItem.id || index}
              navItem={navItem}
              active={active}
            />
          ))
        ) : (
          <DesktopSiteNavItemLoading />
        )}
      </div>
    </nav>
  );
};
